import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Calendar, CreditCard, Shield, ArrowRight } from 'lucide-react';
import { getCurrentUser } from '../lib/auth';
import { verifySubscription } from '../lib/stripe';
import { USER_LIMITS } from '../lib/types';
import Layout from '../components/Layout';

const SubscriptionSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const subscription = user ? USER_LIMITS[user.role] : null;

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setError('Session non valide');
        setIsLoading(false);
        return;
      }

      try {
        await verifySubscription(sessionId);
        setIsLoading(false);
      } catch (error) {
        setError('Erreur lors de la vérification de l\'abonnement');
        setIsLoading(false);
      }
    };

    verifySession();
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Une erreur est survenue</h1>
          <p className="text-gray-600 mb-6">{error || 'Abonnement non trouvé'}</p>
          <button
            onClick={() => navigate('/pricing')}
            className="btn-primary"
          >
            Retour aux tarifs
          </button>
        </div>
      </div>
    );
  }

  const startDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <Layout>
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Merci pour votre abonnement !
              </h1>
              <p className="text-gray-600">
                Votre abonnement {subscription.name} est maintenant actif.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Détails de l'abonnement</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start">
                    <CreditCard className="w-5 h-5 text-indigo-600 mt-1 mr-3" />
                    <div>
                      <p className="font-medium text-gray-800">Plan {subscription.name}</p>
                      <p className="text-gray-600">{subscription.price}€ / mois</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 text-indigo-600 mt-1 mr-3" />
                    <div>
                      <p className="font-medium text-gray-800">Période</p>
                      <p className="text-gray-600">Du {startDate} au {endDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Fonctionnalités incluses</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscription.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-indigo-50 rounded-lg p-6">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-indigo-600 mt-1 mr-4" />
                  <div>
                    <h3 className="font-semibold text-indigo-900 mb-1">Garantie satisfait ou remboursé</h3>
                    <p className="text-indigo-700">
                      Vous bénéficiez d'une garantie de remboursement de 14 jours si vous n'êtes pas satisfait.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center pt-6">
                <button
                  onClick={() => navigate('/pimper')}
                  className="btn-primary flex items-center space-x-2"
                >
                  <span>Commencer à générer des offres</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default SubscriptionSuccessPage;